import { forwardRef, useState } from "react";
import { Typography } from "@mui/material";
import {
  GENERATING_CREATIVE_TITLE,
  DISCARD_GENERATING_CREATIVE_ALERT_DESCRIPTION,
  DISCARD_GENERATING_VIDEO_ALERT_DESCRIPTION,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import { openAlertModal } from "../../utils/alertModalsState";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertErrorMessage from "./TaskAlertErrorMessage";
import TaskAlertDiscardActions from "./TaskAlertDiscardActions";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";
import { getCreativeJobAlertConfig } from "../../utils/creativeJobAlertConfig";

const getTaskVisualStatus = ({ isError, isSuccess, isLoading }) => {
  if (isError) return "error";
  if (isSuccess) return "success";
  if (isLoading) return "inProgress";
  return "pending";
};

const GeneratingCreativeAlertItem = forwardRef(({ alert, onClose }, ref) => {
  const { t } = useTranslationApp();
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const { id, status, progress, generationData, message, messageKey, jobID } =
    alert;
  const config = getCreativeJobAlertConfig(alert.jobType);
  const isVideo = (generationData?.mediaType || config.mediaType) === "video";

  const isError = status === ALERTS_ASYNC_STATUS.ERROR;
  const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
  const isLoading = status === ALERTS_ASYNC_STATUS.LOADING;
  const isTerminal = isError || isSuccess;
  const storageKey = jobID ?? id;

  const {
    loaderTimeMs,
    onProgressChange,
    seedProgress,
    formattedRemainingTime,
  } = useCreativeJobProgressLoader({
    storageKey,
    jobType: alert.jobType,
    isTerminal,
    itemCount:
      generationData?.sourceImageCount ??
      generationData?.itemCount ??
      generationData?.items?.length,
    hasPrompt: Boolean(generationData?.prompt),
  });

  const getTranslatedMessage = () => {
    if (isError) return t(config.messageKeys.error);
    if (isSuccess) return t(config.messageKeys.success);
    if (messageKey) return t(messageKey);
    return t(config.messageKeys.loading || GENERATING_CREATIVE_TITLE);
  };

  const onAlertClick = () => {
    if (isConfirmingDiscard) return;

    openAlertModal(id, generationData, () => {});
  };

  const taskVisualStatus = getTaskVisualStatus({
    isError,
    isSuccess,
    isLoading,
  });
  const displayProgress = progress || (isLoading ? seedProgress : 100);
  const showDiscardButton = isError || isSuccess;
  const discardDescriptionKey = isVideo
    ? DISCARD_GENERATING_VIDEO_ALERT_DESCRIPTION
    : DISCARD_GENERATING_CREATIVE_ALERT_DESCRIPTION;

  return (
    <TaskAlertCard
      ref={ref}
      status={taskVisualStatus}
      label={getTranslatedMessage()}
      estimatedTime={isLoading ? formattedRemainingTime : null}
      progress={displayProgress}
      onClick={onAlertClick}
      cursor="pointer"
      showProgressBar={!isLoading}
      showProgressPercent={!isLoading}
      progressSlot={
        isLoading ? (
          <LinearLoaderWithTime
            isAlert={true}
            labelSide="bottom"
            time={loaderTimeMs}
            progress={seedProgress}
            autoIncrement={true}
            max={99}
            onProgressChange={onProgressChange}
          />
        ) : null
      }
      details={
        message ? (
          isError ? (
            <TaskAlertErrorMessage>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontSize: "0.75rem" }}
              >
                {message}
              </Typography>
            </TaskAlertErrorMessage>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {message}
            </Typography>
          )
        ) : null
      }
      actions={
        showDiscardButton ? (
          <TaskAlertDiscardActions
            isConfirming={isConfirmingDiscard}
            descriptionKey={discardDescriptionKey}
            onStartDiscard={() => {
              if (isSuccess) {
                setIsConfirmingDiscard(true);
                return;
              }
              onClose(id);
            }}
            onConfirm={() => onClose(id)}
            onCancel={() => setIsConfirmingDiscard(false)}
            onView={isSuccess ? onAlertClick : null}
          />
        ) : null
      }
    />
  );
});

GeneratingCreativeAlertItem.displayName = "GeneratingCreativeAlertItem";

export default GeneratingCreativeAlertItem;
